import { FormControl, Select } from '@material-ui/core';
import MenuItem from '@material-ui/core/MenuItem';
import { makeStyles } from '@material-ui/core/styles';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import i18next from 'i18next';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';

import flagDe from '@/assets/Icons/flag_de.svg';
import flagEn from '@/assets/Icons/flag_en.svg';

import { Context as AuthContext } from '@/context/AuthContext.js';
import { ContextProvider } from '@/context/ProjectContext.jsx';

import { LIGHT_BORDER } from '@/theme/index.ts';

import { processAPIWithHeaders } from '@/utils/helpers.js';

import { usePersistedPrismicStore } from '@/store.js';

const LANGUAGE_META = {
    de: { flag: flagDe, label: 'Deutsch' },
    en: { flag: flagEn, label: 'English' },
};

const useStyles = makeStyles((theme) => ({
    wrapper: {
        display: 'flex',
        alignItems: 'center',
        marginRight: '1rem',
        border: 'none',
        boxShadow: 'none',
        borderRadius: theme.buttonShape.radius,
    },
    dropdown: {
        minWidth: '6rem',
        minHeight: theme.buttonShape.height,
        border: theme.border.light,
        background: 'white',
        padding: '0 2rem 0 1rem !important',
        boxShadow: 'none',
        display: 'flex',
        borderRadius: theme.buttonShape.radius,
        alignItems: 'center',
    },
    dropdownSelect: {
        '&:focus': {
            borderRadius: theme.buttonShape.radius,
            backgroundColor: 'transparent',
        },
    },
    dropdownIcon: {
        right: '0.5rem',
        color: 'rgba(46, 46, 46, 0.46)',
    },
    languageOption: {
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
    },
    menuItem: {
        minHeight: 'unset',
        paddingTop: '0.625rem',
        paddingBottom: '0.625rem',
    },
    flag: {
        width: '1.25rem',
        height: '1.25rem',

        flexShrink: 0,
    },
    languageLabel: {
        fontSize: '0.875rem',
        fontWeight: theme.weight.heading,
        lineHeight: 1,
    },
}));

const LanguageSelector = () => {
    const classes = useStyles();
    const { i18n } = useTranslation();
    const {
        state: { usersession, userAccount },
        setuseraccounts,
    } = useContext(AuthContext);
    const { setLanguageGlobal } = useContext(ContextProvider);
    const { [i18n.language]: footerPrismicData } = usePersistedPrismicStore((state) => state.footerData);

    const updateUserAccount = async (language) => {
        const updateAccount = await processAPIWithHeaders(`secure/user/accounts/${userAccount.user_id}`, 'POST', {
            body: {
                user_id: userAccount.user_id,
                privacy_policy_status: userAccount.privacy_policy_status,
                terms_and_conditions_status: userAccount.terms_and_conditions_status,
                use_of_analytics_status: userAccount.use_of_analytics_status,
                verification_status: userAccount.verification_status,
                locale: {
                    language: language,
                    country: userAccount.locale.country,
                },
                status: userAccount.status,
            },
            headers: {
                'Content-Type': 'application/json',
                Authorization: usersession.idToken.jwtToken,
            },
        });
        setuseraccounts(updateAccount);
    };

    const updateLanguage = (language = '') => {
        if (language !== '' && language !== i18n.language) {
            i18next.changeLanguage(language, (err) => {
                if (err) {
                    console.debug('something went wrong loading', err);
                    return;
                }

                setLanguageGlobal(language);
                localStorage.setItem('languageGlobal', language);
            });
            if (usersession) {
                updateUserAccount(language);
            }
        }
    };

    if (!footerPrismicData?.dataSet) return null;

    return (
        <FormControl className={classes.wrapper}>
            <Select
                disableUnderline
                onChange={(e) => updateLanguage(e.target.value)}
                value={i18n.language}
                IconComponent={KeyboardArrowDownIcon}
                classes={{ root: classes.dropdown, select: classes.dropdownSelect, icon: classes.dropdownIcon }}
                MenuProps={{
                    elevation: 0,
                    disableScrollLock: false,
                    getContentAnchorEl: null,
                    MenuListProps: {
                        disablePadding: true,
                    },
                    PaperProps: {
                        style: {
                            boxShadow: 'none',
                            border: LIGHT_BORDER,
                        },
                    },
                    anchorOrigin: {
                        vertical: 'bottom',
                        horizontal: 'left',
                    },
                    transformOrigin: {
                        vertical: 'top',
                        horizontal: 'left',
                    },
                }}
                renderValue={(value) => {
                    const meta = LANGUAGE_META[value];
                    return (
                        <span className={classes.languageOption}>
                            {meta?.flag && <img className={classes.flag} src={meta.flag} alt={`${value} flag`} />}
                            <span className={classes.languageLabel}>{meta?.label ?? value}</span>
                        </span>
                    );
                }}
                data-testid="language-switcher"
            >
                {footerPrismicData.content.language_values.map((languageOption) => {
                    const meta = LANGUAGE_META[languageOption.language_code];
                    return (
                        <MenuItem
                            key={languageOption.language_code}
                            disableRipple
                            value={languageOption.language_code}
                            className={classes.menuItem}
                        >
                            <span className={classes.languageOption}>
                                {meta?.flag && (
                                    <img
                                        className={classes.flag}
                                        src={meta.flag}
                                        alt={`${languageOption.language_code} flag`}
                                    />
                                )}
                                <span className={classes.languageLabel}>
                                    {meta?.label ?? languageOption.display_value[0].text}
                                </span>
                            </span>
                        </MenuItem>
                    );
                })}
            </Select>
        </FormControl>
    );
};

export default LanguageSelector;
